import { FunctionComponent, MouseEvent, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { usePageBlur } from 'hooks';
import {
  SidePanelBody,
  SidePanelCloseButton,
  SidePanelDrawer,
  SidePanelHeader,
  SidePanelTitle,
  StyledSidePanel,
} from './SidePanel.styled';
import { SidePanelProps } from './types';

export const SidePanel: FunctionComponent<SidePanelProps> = props => {
  const { canClose = true, children, open, title, onClose, onClosed, onOpen } = props;

  // Get reference to the parent element of the side panel
  const parentElement = useMemo(() => document.getElementById('root') ?? document.body, []);

  // Retrieve page content blur helpers
  const { pageBlur, pageUnblur } = usePageBlur();

  // References to main and drawer elements
  const drawerRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Retrieve application theme
  const theme = useTheme();

  /**
   * Disable context menu appearing on the overlay (mainly fixes long pressing on table rows)
   *
   * @param {MouseEvent} event Mouse event object
   */
  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLDivElement>) => event.preventDefault(),
    [],
  );

  /**
   * Handle clicks on the overlay element
   *
   * @param {MouseEvent} event Event object
   */
  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!canClose) {
        return;
      }

      // Only close the side panel when clicking outside the panel ref
      if (!drawerRef.current?.contains(event.target as Node)) {
        onClose && onClose();
      }
    },
    [canClose, onClose],
  );

  return createPortal(
    <CSSTransition
      in={open}
      mountOnEnter={true}
      nodeRef={ref}
      timeout={parseInt(theme.transition.medium)}
      unmountOnExit={true}
      onEnter={pageBlur}
      onEntering={onOpen}
      onExit={pageUnblur}
      onExited={onClosed}
    >
      <StyledSidePanel ref={ref} onContextMenu={handleContextMenu} onMouseDown={handleMouseDown}>
        <SidePanelDrawer ref={drawerRef}>
          <SidePanelHeader>
            <SidePanelTitle>{title}</SidePanelTitle>
            <SidePanelCloseButton disabled={!canClose} onClick={onClose} />
          </SidePanelHeader>
          <SidePanelBody>{children}</SidePanelBody>
        </SidePanelDrawer>
      </StyledSidePanel>
    </CSSTransition>,
    parentElement,
  );
};
